import React, { useEffect } from 'react'
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { validateEmail } from '../auth/validationHelpers';
import { ToastContainer, Zoom, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import cn from '../../utils/cn';
import { validateField } from '../../helpers/validationStratergy';
import { tagsToAvoidOnBlurEvent, toastStyle } from '../../utils/toastUtils';
import InputField from '../input/InputField';
import posthog from 'posthog-js';
import { getErrorCode } from '../../utils/interviewApi';
import { cachedMe, INVITE_PLAN_COPY, inviteErrorMessage, isWithoutOrganisation, landingPathAfterLogin, NO_ORGANISATION_PATH, ORG_ERROR_COPY, orgErrorMessage, requestLoginCode, storeSession, verifyLoginCode } from '../../utils/organisationApi';
import { handleLogout } from '../auth/authHelpers';
import OtpStep from './OtpStep';

const NO_ACCOUNT_MESSAGE = "There's no account with this email.";

const readSignedInEmail = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("user_data") || "null");
    if (stored?.email) return stored.email;
  } catch {
    // Fall through to the cached profile
  }
  return cachedMe()?.user?.email || "";
};

const sameEmail = (left, right) => left.trim().toLowerCase() === right.trim().toLowerCase();

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  // Only an invite link may be carried into this login
  const nextParam = searchParams.get("next") || "";
  const nextPath = nextParam.startsWith("/invite?") ? nextParam : "";
  const inviteToken = nextPath ? new URLSearchParams(nextPath.slice("/invite?".length)).get("token") || null : null;
  const presetEmail = searchParams.get("email") || "";
  const notice = location.state?.notice || "";
  const [formData, setFormData] = useState({ email: presetEmail });
  const [errors, setErrors] = useState({ email: "", apiError: "" });
  const [isLoading, setIsLoading] = useState(false);
  // "email" asks for the address, "code" for the code emailed to it
  const [step, setStep] = useState("email");
  const [noAccount, setNoAccount] = useState(false);
  const [sessionEmail, setSessionEmail] = useState(readSignedInEmail);

  const invitedEmail = presetEmail.trim();
  const signedInEmail = sessionEmail.trim();
  const differentAccount = Boolean(
    inviteToken &&
    localStorage.getItem("jwtToken") &&
    invitedEmail &&
    signedInEmail &&
    !sameEmail(signedInEmail, invitedEmail)
  );
  const loginAgainHref = `/login?email=${encodeURIComponent(presetEmail)}&next=${encodeURIComponent(nextPath)}`;

  useEffect(() => {
    // An invite has to be sent with this login, so don't skip the form
    if (inviteToken) return;
    const token = localStorage.getItem("jwtToken");
    if (token) {
      const signedInPath = isWithoutOrganisation() ? NO_ORGANISATION_PATH : "/";
      navigate(signedInPath, { replace: true })
    }
  }, [navigate, inviteToken])

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prevErrors) => ({ ...prevErrors, [name]: validateField(name, value), apiError: "" }));
    setNoAccount(false);
  };

  // Returns an error message, or nothing once the code has been emailed
  const sendCode = async () => {
    try {
      await requestLoginCode({ email: formData.email, inviteToken });
      return "";
    } catch (err) {
      if (getErrorCode(err) === "USER_NOT_FOUND") return NO_ACCOUNT_MESSAGE;
      return orgErrorMessage(err, "We couldn't send a code. Try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailError = validateEmail(formData.email);
    setErrors({ email: emailError, apiError: "" });
    setNoAccount(false);
    if (emailError) return;

    setIsLoading(true);
    const message = await sendCode();
    setIsLoading(false);
    if (!message) {
      setStep("code");
      return;
    }
    if (message === NO_ACCOUNT_MESSAGE) {
      setErrors({ email: message, apiError: "" });
      setNoAccount(true);
      return;
    }
    toast.error(message, { style: toastStyle });
    setErrors({ email: "", apiError: message });
  };

  const handleVerify = async (otp) => {
    let data;
    try {
      data = await verifyLoginCode({ email: formData.email, otp, inviteToken });
    } catch (err) {
      if (getErrorCode(err) === "ORGANISATION_SUSPENDED") return ORG_ERROR_COPY.ORGANISATION_SUSPENDED;
      // The invite can't take a seat: say so and who can fix it
      if (inviteToken && INVITE_PLAN_COPY[getErrorCode(err)]) return INVITE_PLAN_COPY[getErrorCode(err)];
      return orgErrorMessage(err, "That code is wrong or has expired.");
    }
    const user = storeSession(data);
    if (!user) return "We couldn't sign you in. Try again.";

    posthog.identify(user.email, {
      user_id: user.user_id,
      first_name: user.first_name,
      last_name: user.last_name,
    });

    setSessionEmail(user.email || "");
    // The invite belongs to a different address, so stay here and show who is signed in.
    if (inviteToken && invitedEmail && user.email && !sameEmail(user.email, invitedEmail)) {
      setStep("email");
      return "";
    }

    const inviteNotice = inviteErrorMessage(data.invite_error);
    // The token is for the organisation used last, or the one the invite just joined
    navigate(landingPathAfterLogin(), {
      replace: true,
      state: inviteNotice ? { inviteNotice } : undefined,
    });
    return "";
  };

  const backToEmail = () => {
    setStep("email");
    setErrors({ email: "", apiError: "" });
  };

  const handleBlur = (e) => {
    setTimeout(() => {
      const nextElement = document.activeElement;
      if (!tagsToAvoidOnBlurEvent.includes(nextElement.tagName)) {
        handleChange(e);
      }
    }, 0);
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <img
        src="/sift-hire-logo.png"
        alt="Logo"
        className="logo w-48 mb-4 sm:mb-6"
      />
      <div className="mx-auto w-full max-w-md">
        <div className="bg-white px-6 py-12 sm:px-12 shadow sm:rounded-lg border border-accent-gray-border">
          {differentAccount ? (
            <div className="text-center">
              <h1 className="text-xl font-semibold text-black">You're logged in with a different account</h1>
              <p className="text-sm text-accent-gray-medium mt-3">
                This invitation was sent to a different email address.
              </p>
              <div className="mt-6 flex flex-col gap-4 text-left">
                <div className="rounded-md border border-[#d5deec] bg-[#f4f7fb] px-4 py-3">
                  <p className="text-xs text-accent-gray-medium">Currently logged in as</p>
                  <p className="mt-1 text-sm font-semibold text-black">{signedInEmail}</p>
                </div>
                <div className="rounded-md border border-[#d5deec] bg-[#f4f7fb] px-4 py-3">
                  <p className="text-xs text-accent-gray-medium">Invitation sent to</p>
                  <p className="mt-1 text-sm font-semibold text-black">{invitedEmail}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleLogout(loginAgainHref)}
                className="mt-6 w-full py-2.5 px-4 text-base font-semibold rounded-md text-white bg-accent-blue hover:bg-accent-blue-hover"
              >
                Sign out
              </button>
              <button
                type="button"
                onClick={() => navigate(landingPathAfterLogin(), { replace: true })}
                className="mt-3 w-full py-2.5 px-4 text-base font-semibold rounded-md text-accent-blue border border-[#c5d4ef] hover:bg-[#f4f7fc]"
              >
                Continue with this account
              </button>
            </div>
          ) : step === "code" ? (
            <OtpStep
              email={formData.email.trim()}
              onVerify={handleVerify}
              onResend={sendCode}
              onBack={backToEmail}
              verifyLabel="Log in"
              verifyingLabel="Logging in"
            />
          ) : (
          <form onSubmit={handleSubmit}>
            {notice ? (
              <p className="text-sm text-center text-accent-gray-medium mb-4" role="status">
                {notice}
              </p>
            ) : null}
            <InputField
              id="email"
              name="email"
              placeholder="Enter your email address"
              type="name"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              autoFocus={!presetEmail && !inviteToken}
              disabled={Boolean(inviteToken)}
              className={inviteToken ? "bg-[#f4f7fb] text-accent-gray-medium cursor-not-allowed" : ""}
              errorState={errors.email}
              label={"Email Address"}
            />
            {/* {noAccount && (
              <p className="text-sm text-accent-gray-medium mt-1">
                <Link to="/signup" className="text-accent-blue font-semibold hover:underline">
                  Create an account
                </Link>
                {" "}to get started.
              </p>
            )} */}
           
            <div className="mt-6 flex">
              <span className="block w-full rounded-md shadow-sm">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={cn("w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent text-base font-semibold rounded-md text-white transition duration-150 ease-in-out bg-accent-blue",
                    isLoading ? "cursor-not-allowed" : "hover:bg-accent-blue-hover")}
                >
                  {isLoading ? (
                    <>
                      Sending code
                      <div className="animate-spin rounded-full border-t-2 border-white h-4 w-4"></div>
                    </>
                  ) : (
                    <>
                      Send OTP
                      <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.5 3.5H5.5C4.39543 3.5 3.5 4.39543 3.5 5.5V19.5C3.5 20.6046 4.39543 21.5 5.5 21.5H19.5C20.6046 21.5 21.5 20.6046 21.5 19.5V5.5C21.5 4.39543 20.6046 3.5 19.5 3.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.5 12.5H16.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12.5 16.5L16.5 12.5L12.5 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </>
                  )}
                </button>
              </span>
            </div>
          </form>
          )}
        </div>
        {!differentAccount && step === "email" && (
          <p className="text-sm mt-3 text-center text-accent-gray-medium">
            New to Sift Hire?{" "}
            <Link to="/signup" className="text-accent-blue font-semibold hover:underline">
              Sign up
            </Link>
          </p>
        )}
      </div>
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar
        transition={Zoom}
        closeOnClick
        draggable
        closeButton={false}
        limit={1}
      />
    </div>
  );
};

export default Login
